import { ref, isRef, Ref } from "vue"

/** Anything that can be shown and hidden -- e.g. a Modal.vue instance (its exposed open/close). */
export interface ModalControl {
    open?(): void
    close?(): void
}

export interface Confirmation {
    /** True while a decision is pending -- bind to a modal whose visibility is reactive. */
    readonly isOpenRef: Ref<boolean>
    /** Show the modal (via the `modal` argument) and resolve once the user decides. */
    ask(): Promise<boolean>
    /** Resolve the pending decision as confirmed. Wire to the modal's accept/ok action. */
    confirm(): void
    /** Resolve the pending decision as dismissed. Wire to the modal's close/hidden event. */
    dismiss(): void
}

/**
 * An awaitable yes/no confirmation, decoupled from any particular modal. A caller awaits `ask()`
 * and wires `confirm`/`dismiss` to whatever accept and close signals its modal emits.
 *
 * Drive visibility either reactively (bind `isOpenRef`) or imperatively by passing a control: a
 * `{ open, close }` object, or a ref to one (a Modal.vue template ref works directly, since it
 * exposes `open`/`close`). `close` runs only when a still-pending decision is settled by
 * `confirm()`, never from `dismiss()` -- dismissal is normally the modal reporting it has already
 * closed, and closing an already-hidden modal can hang.
 */
export function useConfirmation(modal?: ModalControl | Ref<ModalControl | undefined>): Confirmation {
    const control = () => (isRef(modal) ? modal.value : modal)
    let resolve: ((confirmed: boolean) => void) | undefined
    const isOpenRef = ref(false)

    function ask() {
        resolve?.(false) // settle any prior prompt rather than leave it hanging
        isOpenRef.value = true
        control()?.open?.()
        return new Promise<boolean>((r) => { resolve = r })
    }

    function settle(confirmed: boolean, close: boolean) {
        if (!resolve) {
            return
        }
        resolve(confirmed)
        resolve = undefined
        isOpenRef.value = false
        if (close) {
            control()?.close?.()
        }
    }

    return {
        isOpenRef,
        ask,
        confirm: () => settle(true, true),
        dismiss: () => settle(false, false),
    }
}
